/**
 * 促销规则域 Mock 数据和 Mock 函数
 */

import type { ApiResponse, PageData } from '../../types/common';
import type {
  SubstituteRuleDTO,
  QuerySubstituteRulePageRequest,
  CreateSubstituteRuleRequest,
  UpdateSubstituteRuleRequest,
  EnableSubstituteRuleRequest,
  DisableSubstituteRuleRequest,
  DeleteSubstituteRuleRequest,
  BatchDeleteSubstituteRuleRequest,
} from '../../types/promotion-rule/substituteRule';
import { SubstituteRuleStatus, SubstituteRuleType } from '../../constants/promotionRule';
import { MatchType, MatchRule, CategoryLevel } from '../../types/promotion-rule/substituteRule';

// Mock 工具函数 - 成功响应
function mockSuccess<T>(data: T): ApiResponse<T> {
  return {
    success: true,
    errorMessage: null,
    flag: 'success',
    data,
    traceId: `mock-${Date.now()}`,
  };
}

// Mock 工具函数 - 失败响应
function mockError(message: string): ApiResponse<null> {
  return {
    success: false,
    errorMessage: message,
    flag: 'fail',
    data: null,
    traceId: `mock-${Date.now()}`,
  };
}

// Mock 数据
const mockSubstituteRules: SubstituteRuleDTO[] = [
  {
    id: 1,
    code: 'SUB001',
    ruleCode: 'SUB001',
    ruleName: 'Rule 001',
    description: '',
    priority: 1,
    effectiveStartTime: Date.now() - 86400000 * 30,
    effectiveEndTime: null,
    status: SubstituteRuleStatus.ENABLED,
    creatorAccount: 'admin',
    creatorName: 'System Admin',
    gmtCreate: Date.now() - 86400000 * 30,
    modifierAccount: 'admin',
    modifierName: 'System Admin',
    gmtModified: Date.now() - 86400000 * 2,
    matchConfigs: [
      { matchType: MatchType.CATEGORY, matchRule: MatchRule.MUST_MATCH, priority: 'L1', categoryLevel: CategoryLevel.L2 },
      { matchType: MatchType.PRICE, matchRule: MatchRule.EQUAL, priority: 'L2' },
      { matchType: MatchType.BRAND, matchRule: MatchRule.MUST_MATCH, priority: 'L3' },
    ],
    applyStoreCodes: ['1101', '1102'],
    applyStoreTypes: [],
    excludeStores: [],
  },
  {
    id: 2,
    code: 'SUB002',
    ruleCode: 'SUB002',
    ruleName: 'Rule 002',
    description: '',
    priority: 1,
    effectiveStartTime: Date.now() - 86400000 * 20,
    effectiveEndTime: Date.now() + 86400000 * 60,
    status: SubstituteRuleStatus.ENABLED,
    creatorAccount: 'user01',
    creatorName: 'John Doe',
    gmtCreate: Date.now() - 86400000 * 20,
    modifierAccount: 'user01',
    modifierName: 'John Doe',
    gmtModified: Date.now() - 86400000 * 5,
    matchConfigs: [
      { matchType: MatchType.CATEGORY, matchRule: MatchRule.MUST_MATCH, priority: 'L1', categoryLevel: CategoryLevel.L1 },
      { matchType: MatchType.BRAND, matchRule: MatchRule.MUST_MATCH, priority: 'L2' },
    ],
    applyStoreCodes: ['1103'],
    applyStoreTypes: [],
    excludeStores: [],
  },
  {
    id: 3,
    code: 'SUB003',
    ruleCode: 'SUB003',
    ruleName: 'Rule 003',
    description: '',
    priority: 2,
    effectiveStartTime: Date.now() - 86400000 * 10,
    effectiveEndTime: null,
    status: SubstituteRuleStatus.DISABLED,
    creatorAccount: 'admin',
    creatorName: 'System Admin',
    gmtCreate: Date.now() - 86400000 * 10,
    modifierAccount: 'admin',
    modifierName: 'System Admin',
    gmtModified: Date.now() - 86400000 * 3,
    matchConfigs: [
      { matchType: MatchType.PRICE, matchRule: MatchRule.LESS_THAN, priority: 'L1' },
      { matchType: MatchType.CATEGORY, matchRule: MatchRule.MUST_MATCH, priority: 'L2', categoryLevel: CategoryLevel.L3 },
    ],
    applyStoreCodes: [],
    applyStoreTypes: ['GF', 'SUPER'],
    excludeStores: ['1105'],
  },
];

// 分页查询替代品规则 Mock
export function mockPageSubstituteRule(params: QuerySubstituteRulePageRequest): Promise<ApiResponse<PageData<SubstituteRuleDTO>>> {
  let filteredData = [...mockSubstituteRules];

  if (params.ruleCode) {
    filteredData = filteredData.filter(item =>
      item.ruleCode.toLowerCase().includes(params.ruleCode!.toLowerCase())
    );
  }
  if (params.ruleName) {
    filteredData = filteredData.filter(item =>
      item.ruleName.toLowerCase().includes(params.ruleName!.toLowerCase())
    );
  }

  if (params.status) {
    filteredData = filteredData.filter(item => item.status === params.status);
  }

  const { currentPage = 1, pageSize = 20 } = params;
  const start = (currentPage - 1) * pageSize;
  const end = start + pageSize;
  const tableData = filteredData.slice(start, end);

  return Promise.resolve(mockSuccess({
    tableData,
    paging: { currentPage, pageSize, totalCount: filteredData.length },
  }));
}

// 查询替代品规则详情 Mock
export function mockGetSubstituteRuleDetail(ruleCode: string): Promise<ApiResponse<SubstituteRuleDTO>> {
  const rule = mockSubstituteRules.find(item => item.ruleCode === ruleCode);
  if (!rule) {
    return Promise.resolve(mockError('规则不存在') as any);
  }
  return Promise.resolve(mockSuccess(rule));
}

// 新建替代品规则 Mock
export function mockCreateSubstituteRule(data: CreateSubstituteRuleRequest): Promise<ApiResponse<SubstituteRuleDTO>> {
  const newRule: SubstituteRuleDTO = {
    id: Date.now(),
    code: `SUB${Date.now()}`,
    ruleCode: `SUB${Date.now()}`,
    ruleName: data.ruleName,
    description: data.description || '',
    priority: data.priority,
    effectiveStartTime: data.effectiveStartTime,
    effectiveEndTime: data.effectiveEndTime || null,
    status: SubstituteRuleStatus.DISABLED,
    creatorAccount: 'admin',
    creatorName: 'System Admin',
    gmtCreate: Date.now(),
    modifierAccount: 'admin',
    modifierName: 'System Admin',
    gmtModified: Date.now(),
    matchConfigs: data.matchConfigs || [],
    applyStoreCodes: data.applyStoreCodes || [],
    applyStoreTypes: data.applyStoreTypes || [],
    excludeStores: data.excludeStores || [],
  };
  mockSubstituteRules.push(newRule);
  return Promise.resolve(mockSuccess(newRule));
}

// 编辑替代品规则 Mock
export function mockUpdateSubstituteRule(data: UpdateSubstituteRuleRequest): Promise<ApiResponse<SubstituteRuleDTO>> {
  const index = mockSubstituteRules.findIndex(item => item.ruleCode === data.ruleCode);
  if (index === -1) {
    return Promise.resolve(mockError('规则不存在') as any);
  }
  const existing = mockSubstituteRules[index];
  const updated: SubstituteRuleDTO = {
    ...existing,
    ruleName: data.ruleName ?? existing.ruleName,
    description: data.description ?? existing.description,
    priority: data.priority ?? existing.priority,
    effectiveStartTime: data.effectiveStartTime ?? existing.effectiveStartTime,
    effectiveEndTime: data.effectiveEndTime ?? existing.effectiveEndTime,
    matchConfigs: data.matchConfigs ?? existing.matchConfigs,
    modifierAccount: 'admin',
    modifierName: 'System Admin',
    gmtModified: Date.now(),
  };
  mockSubstituteRules[index] = updated;
  return Promise.resolve(mockSuccess(updated));
}

// 启用替代品规则 Mock
export function mockEnableSubstituteRule(data: EnableSubstituteRuleRequest): Promise<ApiResponse<SubstituteRuleDTO>> {
  const index = mockSubstituteRules.findIndex(item => item.ruleCode === data.ruleCode);
  if (index === -1) {
    return Promise.resolve(mockError('规则不存在') as any);
  }
  mockSubstituteRules[index] = {
    ...mockSubstituteRules[index],
    status: SubstituteRuleStatus.ENABLED,
    gmtModified: Date.now(),
  };
  return Promise.resolve(mockSuccess(mockSubstituteRules[index]));
}

// 禁用替代品规则 Mock
export function mockDisableSubstituteRule(data: DisableSubstituteRuleRequest): Promise<ApiResponse<SubstituteRuleDTO>> {
  const index = mockSubstituteRules.findIndex(item => item.ruleCode === data.ruleCode);
  if (index === -1) {
    return Promise.resolve(mockError('规则不存在') as any);
  }
  mockSubstituteRules[index] = {
    ...mockSubstituteRules[index],
    status: SubstituteRuleStatus.DISABLED,
    gmtModified: Date.now(),
  };
  return Promise.resolve(mockSuccess(mockSubstituteRules[index]));
}

// 删除替代品规则 Mock
export function mockDeleteSubstituteRule(data: DeleteSubstituteRuleRequest): Promise<ApiResponse<null>> {
  const index = mockSubstituteRules.findIndex(item => item.ruleCode === data.ruleCode);
  if (index > -1) {
    mockSubstituteRules.splice(index, 1);
  }
  return Promise.resolve(mockSuccess(null));
}

// 批量删除替代品规则 Mock
export function mockBatchDeleteSubstituteRules(data: BatchDeleteSubstituteRuleRequest): Promise<ApiResponse<null>> {
  data.ruleCodes.forEach(code => {
    const index = mockSubstituteRules.findIndex(item => item.ruleCode === code);
    if (index > -1) {
      mockSubstituteRules.splice(index, 1);
    }
  });
  return Promise.resolve(mockSuccess(null));
}
